import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { keycloak } from "./Keycloak";
import * as Sentry from "@sentry/react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import PrivateRoute from "./helpers/PrivateRoute";
import SessionExpiredPopup from "./components/element/SessionExpiredPopup";
import { ErrorProvider } from "./services/ErrorProvider";
import { StorageProvider } from "./services/StorageProvider";
import { getServiceUrls } from "./services/serviceUrls";
import { LogoutProvider } from "./services/LogoutProvider";
import { ServerDownProvider } from "./services/ServerDownProvider";
import NotificationHandler from "./components/element/NotificationHandler";
import { FormDataProvider } from "./helpers/FormDataProvider";
import NoNetworkStatusModal from "./components/element/NoNetworkStatusModal";
import UnauthorizedPopup from "./components/element/UnauthorizedPopup";
import ServerDownHandler from "./components/element/ServerDownHandler";
import { LOCALHOST_HOSTNAMES } from "./utils/constants";
import "./components/css/index.css";


// Validate configurations
const validateConfig = (config) => {
  const requiredKeys = [
    "FEE_SERVICE",
    "USER_SERVICE",
    "TRIP_SERVICE",
    "FBO_SERVICE",
    "REACT_APP_KEYCLOAK_URL",
    "REACT_APP_KEYCLOAK_REALM",
    "REACT_APP_KEYCLOAK_CLIENT_ID",
  ];
  requiredKeys.forEach((key) => {
    if (!config[key]) {
      throw new Error(`Missing configuration value for: ${key}`);
    }
  });
};
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      console.info("Service Worker registered:", registration);
    })
    .catch((error) => {
      console.error("Service Worker registration failed:", error);
    });
}
// Initialize Sentry with dynamic configuration
const initializeSentry = () => {
  const config = getServiceUrls();
  const sentryDsn = config.REACT_APP_SENTRY_DSN;

  // Check if running on localhost to prevent local development errors from being sent to Sentry
  const isLocalhost = LOCALHOST_HOSTNAMES.includes(window.location.hostname) ||
    window.location.hostname.includes('localhost');

  // Only initialize Sentry if not on localhost
  if (!isLocalhost && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [],
      tracesSampleRate: 1.0,
      autoSessionTracking: true,
      environment: config.REACT_APP_PROFILE_ENV || "dev",
    });
  }
};

// Initialize Sentry early
initializeSentry();

const root = ReactDOM.createRoot(document.getElementById("root"));

const renderApp = () => {
  const queryClient = new QueryClient();
  const keycloakProviderInitConfig = {
    onLoad: "check-sso",
  };
  const kc = keycloak();
   // Check if current path is health check
   const currentPath = window.location.pathname;
   if (currentPath === "/health") {
     // Import and render health check directly without authentication
     import("./components/element/HealthCheck").then(
       ({ default: HealthCheck }) => {
         root.render(<HealthCheck />);
       }
     );
     return;
   }
 

  root.render(
    <ReactKeycloakProvider
      authClient={kc}
      initOptions={keycloakProviderInitConfig}
    >
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <ServerDownProvider>
            <ErrorProvider>
              <LogoutProvider>
                <ServerDownHandler />
                <SessionExpiredPopup />
                <UnauthorizedPopup />
                <NoNetworkStatusModal />
                <StorageProvider>
                  <FormDataProvider>
                    <NotificationHandler />
                    <PrivateRoute component={App}></PrivateRoute>
                  </FormDataProvider>
                </StorageProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </LogoutProvider>
            </ErrorProvider>
          </ServerDownProvider>
        </QueryClientProvider>
      </React.StrictMode>
    </ReactKeycloakProvider>
  );
};

try {
  const config = getServiceUrls();
  validateConfig(config);
  renderApp();
} catch (error) {
  console.error("Configuration validation failed:", error.message);

  root.render(
    <div style={{ textAlign: "center", marginTop: "20%" }}>
      <h1>Configuration Error</h1>
      <p>{error.message}</p>
      <p>Please contact the administrator or try refreshing the page.</p>
    </div>
  );
}

reportWebVitals();
